import { useContext } from "react";
import { useTranslation } from "react-i18next";

import { IPropertyOptions, PaymentTypes, DistanceUnit, LaneAllocationSystem, IWorkflowRuleOptions, IKeyAuditCriteria, IDefaultBookingDuration } from "../logic/api";
import { ReleaseFlag, useReleaseManagement } from "./feature-management";
import { SessionContext } from "./useSession";
import { Nullable } from "../types/utility";

interface IPreferencesHelper {
  usesPaymentMethod(method: unknown): boolean;
  isEligibleCardPaymentMethod(
    method: unknown
  ): method is
    | PaymentTypes.creditCard
    | PaymentTypes.debitCard
    | PaymentTypes.creditDebitCard;
  isEligibleRefundPaymentMethod(method: unknown): method is PaymentTypes.refund;
  isEligibleAccountPaymentMethod(
    method: unknown
  ): method is PaymentTypes.account;
  isFlightLookupEnabled: boolean;
  isUsingDynamicProductCatalog(): boolean;
  usesDVLALookUp(): boolean;
  isPackageManagementSupported(): boolean;
  isProductManagementSupported(): boolean;
  isUpsellsManagementSupported(): boolean;
  usesCustomerLookUp(): boolean;
  isRecordPropertyVisible(
    record:
      | unknown
      | undefined,
    property: string
  ): boolean;
  isRecordPropertyMandatory(
    record:
      | unknown
      | undefined,
    property: string
  ): boolean;
  displayMixedLaneWarning: boolean;
  displayNextMovementDate: boolean;
  timeZone: string;
  dateFormat: string;
  dateTimeFormat: string;
  defaultBookingDuration: IDefaultBookingDuration | null;
  defaultBookingDurationUsesMidnight: boolean;
  displayCleanAirCompliant: boolean;
  isBookingEndTimeLocked: boolean;
  kmosVersion?: string;
  kmosManualAllocationEnabled?: boolean;
  kmosPromptReAllocateEnabled?: boolean;
  kmosDaysToDisplay?: number;
  pickingOffset: number;
  autoMaskPIIDays: number;
  enable_device_zebra_labels: boolean;
  enable_kiosk_labels: boolean;
  enable_enforce_key_position_audit: boolean;
  distanceUnit: DistanceUnit
  distanceUnitText: string;
  distanceUnitSingular: string;
  distancePerHour: string;
  keymovements_key_then_location: boolean;
  taskAssignmentMarksOnShiftEnabled: boolean;
  allocationSystem: LaneAllocationSystem;
  formatDistance: (value?: number) => Nullable<string>;
  workflowRules?: IWorkflowRuleOptions;
  useTrackingId?: boolean;
  keyAuditSettings?: IKeyAuditCriteria;
  trackingIdRegex?: string;
}

export function usePreferences(): IPreferencesHelper {
  const { t } = useTranslation()
  const session = useContext(SessionContext);
  const preferences = session.current.site?.preferences;
  const isDefaultBookingDurationReleased = useReleaseManagement(ReleaseFlag.releaseDefaultBookingDuration);
  const isDefaultBookingDurationWithMidnightReleased = useReleaseManagement(ReleaseFlag.releaseDefaultBookingDurationWithMidnight);

  const usesPaymentMethod = (method: unknown): boolean => {
    if (!preferences || typeof method !== "number") {
      return false;
    }

    switch (method) {
      case PaymentTypes.account:
        return Boolean(preferences.payment_type_account_enabled);

      case PaymentTypes.cash:
        return Boolean(preferences.payment_type_cash_enabled);

      case PaymentTypes.compDiscount:
        return Boolean(preferences.payment_type_comp_enabled);

      case PaymentTypes.creditCard:
        return Boolean(preferences.payment_type_credit_card_enabled);

      case PaymentTypes.creditDebitCard:
        return Boolean(preferences.payment_type_credit_debit_card_enabled);

      case PaymentTypes.debitCard:
        return Boolean(preferences.payment_type_debit_card_enabled);

      case PaymentTypes.refund:
        return Boolean(preferences.payment_type_refund_enabled);

      default:
        return false;
    }
  };

  const isEligibleCardPaymentMethod = (
    method: unknown
  ): method is
    | PaymentTypes.creditCard
    | PaymentTypes.debitCard
    | PaymentTypes.creditDebitCard => {
    return (
      usesPaymentMethod(method) &&
      (method === PaymentTypes.creditCard ||
        method === PaymentTypes.debitCard ||
        method === PaymentTypes.creditDebitCard)
    );
  };

  const isEligibleRefundPaymentMethod = (
    method: unknown
  ): method is PaymentTypes.refund => {
    return usesPaymentMethod(method) && method === PaymentTypes.refund;
  };

  const isEligibleAccountPaymentMethod = (
    method: unknown
  ): method is PaymentTypes.account => {
    return usesPaymentMethod(method) && method === PaymentTypes.account;
  };

  const isFlightLookupEnabled =
    preferences?.booking_flightLookup_enabled || false;

  const isUsingDynamicProductCatalog = (): boolean => {
    return Boolean(preferences?.product_catalogue_provider_config);
  };

  const isPackageManagementSupported = (): boolean => {
    const supportedProviders = new Set(["parkit"]);
    return supportedProviders.has(
      preferences?.product_catalogue_provider_packages || ""
    );
  };

  const isProductManagementSupported = (): boolean => {
    const supportedProviders = new Set(["parkit"]);
    return supportedProviders.has(
      preferences?.product_catalogue_provider_products || ""
    );
  };
  const isUpsellsManagementSupported = (): boolean => {
    const supportedProviders = new Set(["parkit"]);
    return supportedProviders.has(
      preferences?.product_catalogue_provider_products || ""
    );
  };

  const usesDVLALookUp = (): boolean => {
    return Boolean(preferences?.booking_vehicle_dvla_enabled);
  };

  const usesCustomerLookUp = (): boolean => {
    return Boolean(preferences?.customer_look_up_enabled);
  };

  function isRecordPropertyVisible<
    T extends { properties: Record<string, IPropertyOptions> }
  >(record: T, property: keyof T["properties"]) {
    return Boolean(record?.properties[property as string]);
  }

  function isRecordPropertyMandatory<
    T extends { properties: Record<string, IPropertyOptions> }
  >(record: T, property: keyof T["properties"]) {
    return Boolean(record?.properties[property as string]?.required);
  }

  const displayCleanAirCompliant = Boolean(
    preferences?.displayCleanAirCompliant
  );
  const displayMixedLaneWarning = Boolean(
    preferences?.display_mixed_lane_warning
  );
  const displayNextMovementDate = Boolean(
    preferences?.display_next_movement_date
  );

  const timeZone =
    preferences?.time_zone || Intl.DateTimeFormat().resolvedOptions().timeZone;

  const dateFormat = preferences?.date_format_without_time || "%d/%m";

  const dateTimeFormat = preferences?.date_format || "%d/%m %H:%I";

  const defaultBookingDuration = isDefaultBookingDurationReleased &&
    preferences?.defaultBookingDuration?.enabled
    ? preferences.defaultBookingDuration
    : null;

  const defaultBookingDurationUsesMidnight =
    isDefaultBookingDurationWithMidnightReleased &&
    preferences?.overstays_tolerance_midnight === true;

  const isBookingEndTimeLocked = Boolean(preferences?.lockBookingEndTime);

  const kmosVersion = preferences?.kmos_version;
  const kmosDaysToDisplay = preferences?.kmos_days_to_display;
  const kmosManualAllocationEnabled = preferences?.kmos_manual_allocation_enabled == true;
  const kmosPromptReAllocateEnabled = preferences?.kmos_prompt_reallocate_enabled == true;

  const allocationSystem = preferences?.lane_allocation_system || LaneAllocationSystem.None;

  const pickingOffset = preferences?.picking_offset || 0;

  const autoMaskPIIDays = Number(preferences?.auto_maskPII_days_after_CheckOutDate);
  const enable_device_zebra_labels = preferences?.enable_device_zebra_labels == true;
  const enable_kiosk_labels = preferences?.enable_kiosk_labels == true;
  const enable_enforce_key_position_audit = preferences?.enable_enforce_key_position_audit == true;
  const workflowRules = preferences?.workflowRules;

  const distanceUnit = preferences?.distance_unit || DistanceUnit.miles
  const keymovements_key_then_location = preferences?.keymovements_key_then_location == true
  const taskAssignmentMarksOnShiftEnabled = Boolean(
    preferences?.driver_availability_options?.properties?.task_assignment_marks_on_shift?.required
  );
  let distanceUnitText = ''
  let distanceUnitSingular = ''
  let distancePerHour = ''
  if (distanceUnit === DistanceUnit.km) {
    distanceUnitText = t('distanceUnit.km')
    distanceUnitSingular = t('distanceUnit.km')
    distancePerHour = 'kmh'
  }
  if (distanceUnit === DistanceUnit.miles) {
    distanceUnitText = t('distanceUnit.miles')
    distanceUnitSingular = t('distanceUnit.mileSingular')
    distancePerHour = 'mph'
  }

  const formatDistance = (value?: number): Nullable<string> => {
    return `${value || 0}${distancePerHour}`
  }

  const useTrackingId = preferences?.use_tracking_id;
  const keyAuditSettings = preferences?.key_audit_criteria;
  const trackingIdRegex = preferences?.tracking_id_regex;

  return {
    usesPaymentMethod,
    isEligibleCardPaymentMethod,
    isEligibleRefundPaymentMethod,
    isEligibleAccountPaymentMethod,
    isFlightLookupEnabled,
    isUsingDynamicProductCatalog,
    usesDVLALookUp,
    isPackageManagementSupported,
    isProductManagementSupported,
    isUpsellsManagementSupported,
    usesCustomerLookUp,
    isRecordPropertyVisible,
    isRecordPropertyMandatory,
    displayCleanAirCompliant,
    displayMixedLaneWarning,
    displayNextMovementDate,
    timeZone,
    dateFormat,
    dateTimeFormat,
    defaultBookingDuration,
    defaultBookingDurationUsesMidnight,
    isBookingEndTimeLocked,
    kmosVersion,
    kmosManualAllocationEnabled,
    kmosPromptReAllocateEnabled,
    kmosDaysToDisplay,
    pickingOffset,
    autoMaskPIIDays,
    enable_device_zebra_labels,
    enable_kiosk_labels,
    enable_enforce_key_position_audit,
    distanceUnit,
    distanceUnitText,
    distanceUnitSingular,
    distancePerHour,
    formatDistance,
    keymovements_key_then_location,
    taskAssignmentMarksOnShiftEnabled,
    allocationSystem,
    workflowRules,
    useTrackingId,
    keyAuditSettings,
    trackingIdRegex
  };
}
